// GAVION ai-email (spec §4): « ✨ Rédiger avec l'IA » above the editor, an optional one-line instruction, then
// « Régénérer » (another version, same context and instruction). Typed text is never replaced without confirmation.
// One request at a time (a ref guards double clicks); while it runs the bar is disabled and the parent is told
// (onBusyChange), so the drawer locks the editor and Objet (F11: nothing typed during the call is silently replaced).
import { Confirm } from "@/components/admin/confirm";
import { useTranslate } from "ra-core";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAiApi } from "../../ai/api/AiApiContext";
import { aiErrorMessage, type AiDraftResult } from "../../ai/api/types";

type Props = {
  companyId: number;
  contactId: number | null;
  dealId: number | null;
  /** True when the editor holds text the rep typed (not the last AI text). */
  needsConfirm: () => boolean;
  onDraft: (r: AiDraftResult) => void;
  disabled?: boolean;
  /** True while a draft request runs (the drawer locks its fields). */
  onBusyChange?: (busy: boolean) => void;
};

export const AiDraftBar = ({
  companyId,
  contactId,
  dealId,
  needsConfirm,
  onDraft,
  disabled,
  onBusyChange,
}: Props) => {
  const t = useTranslate();
  const api = useAiApi();
  const [instruction, setInstruction] = useState("");
  const [variant, setVariant] = useState(0);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [rationale, setRationale] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const run = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError(null);
    try {
      const r = await api.draft({
        companyId,
        contactId,
        dealId,
        instruction,
        variant,
      });
      setVariant((v) => v + 1);
      setRationale(r.rationale);
      onDraft(r);
    } catch (e) {
      setError(aiErrorMessage(t, e)); // a code-based message, never draft text
    } finally {
      inFlight.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };
  const click = () => {
    if (inFlight.current) return;
    if (needsConfirm()) setAsking(true);
    else void run();
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Label htmlFor="ai-instruction">
            {t("gavion.ai.compose.instruction")}
          </Label>
          <Input
            id="ai-instruction"
            value={instruction}
            maxLength={300}
            disabled={busy || disabled}
            placeholder={t("gavion.ai.compose.instruction_placeholder")}
            onChange={(e) => setInstruction(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={click}
          disabled={busy || disabled}
          aria-busy={busy}
        >
          {busy
            ? t("gavion.ai.compose.drafting")
            : variant === 0
              ? t("gavion.ai.compose.draft")
              : t("gavion.ai.compose.regenerate")}
        </Button>
      </div>
      {rationale && (
        <p className="text-xs text-muted-foreground" data-testid="ai-rationale">
          {t("gavion.ai.compose.rationale", { text: rationale })}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <Confirm
        isOpen={asking}
        title="gavion.ai.compose.replace_title"
        content="gavion.ai.compose.replace_body"
        onClose={() => setAsking(false)}
        onConfirm={() => {
          setAsking(false);
          void run();
        }}
      />
    </div>
  );
};
